// =============================================================================
// AddressAutocomplete - Google Places autocomplete with fallback
// v5.4: Fixed to use legacy API which is still supported for existing projects
// =============================================================================

import { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Loader2 } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

export interface AddressResult {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  formatted?: string;
}

interface AddressAutocompleteProps {
  /** Callback when address is selected or changed */
  onAddressSelect: (address: AddressResult) => void;
  /** Initial street value */
  defaultValue?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Error state */
  error?: boolean;
}

// =============================================================================
// Google Places Helper Functions
// =============================================================================

/**
 * Check if Google Places API is loaded and available
 */
function isGooglePlacesAvailable(): boolean {
  try {
    return !!(
      typeof window !== 'undefined' &&
      window.google &&
      window.google.maps &&
      window.google.maps.places &&
      window.google.maps.places.Autocomplete
    );
  } catch {
    return false;
  }
}

/**
 * Parse Google address components into our AddressResult format
 */
function parseAddressComponents(
  components: google.maps.GeocoderAddressComponent[]
): AddressResult {
  const get = (type: string, useShortName = false) => {
    const component = components.find(c => c.types.includes(type));
    return useShortName ? component?.short_name || '' : component?.long_name || '';
  };

  const streetNumber = get('street_number');
  const route = get('route');
  const street = streetNumber && route ? `${streetNumber} ${route}` : route || streetNumber;

  return {
    street,
    city: get('locality') || get('sublocality') || get('administrative_area_level_2'),
    state: get('administrative_area_level_1', true),
    zipCode: get('postal_code'),
    country: get('country', true),
  };
}

// =============================================================================
// Component
// =============================================================================

export function AddressAutocomplete({
  onAddressSelect,
  defaultValue = '',
  placeholder = 'Start typing an address...',
  required = false,
  className = '',
  error = false,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [value, setValue] = useState(defaultValue);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if Google Places is available
  useEffect(() => {
    let checkCount = 0;
    const maxChecks = 20; // Check for up to 4 seconds

    const checkGooglePlaces = () => {
      checkCount++;
      if (isGooglePlacesAvailable()) {
        setIsGoogleLoaded(true);
        setIsLoading(false);
      } else if (checkCount >= maxChecks) {
        // Google Places not available after timeout - use fallback
        console.warn('Google Places API not available, using manual input');
        setIsGoogleLoaded(false);
        setIsLoading(false);
      } else {
        // Keep checking
        setTimeout(checkGooglePlaces, 200);
      }
    };

    checkGooglePlaces();
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!isGoogleLoaded || !inputRef.current || autocompleteRef.current) {
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address'],
        types: ['address'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) return;

        const address = parseAddressComponents(place.address_components);
        address.formatted = place.formatted_address;

        setValue(address.street);
        onAddressSelect(address);
      });

      autocompleteRef.current = autocomplete;
    } catch (err) {
      console.error('Failed to initialize Google Places Autocomplete:', err);
      setIsGoogleLoaded(false);
    }

    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        try {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch {
          // Ignore cleanup errors
        }
        autocompleteRef.current = null;
      }
    };
  }, [isGoogleLoaded, onAddressSelect]);

  // Handle manual input change — always propagate street to parent
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    // Always update the parent with the street value so form submission works
    // When Google autocomplete triggers place_changed, it will provide full address
    onAddressSelect({
      street: newValue,
      city: '',
      state: '',
      zipCode: '',
      country: '',
    });
  }, [onAddressSelect]);

  // Base input styles
  const baseClasses = `
    block w-full px-3 py-2 pl-10
    border rounded-md shadow-sm
    focus:outline-none focus:ring-2 focus:ring-[#009FE4] focus:border-[#009FE4]
    transition-colors
  `;

  const errorClasses = error
    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300';

  return (
    <div className="relative">
      {/* Address icon */}
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {isLoading ? (
          <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
        ) : (
          <MapPin className="h-4 w-4 text-gray-400" />
        )}
      </div>

      {/* Input field */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        className={`${baseClasses} ${errorClasses} ${className}`}
      />

      {/* Autocomplete status indicator */}
      {!isLoading && !isGoogleLoaded && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <span className="text-xs text-gray-400" title="Address autocomplete not available">
            Manual entry
          </span>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Google Maps Script Loader
// =============================================================================

/**
 * Dynamically load Google Maps script with Places library
 */
export function loadGoogleMapsScript(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isGooglePlacesAvailable()) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps script')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__googleMapsCallback`;
    script.async = true;
    script.defer = true;

    // Use callback to ensure API is fully initialized
    (window as unknown as { __googleMapsCallback: () => void }).__googleMapsCallback = () => {
      resolve();
    };

    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
}

// Export type for use in other components
export type { AddressAutocompleteProps };
